import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../AboutLocation.module.scss';
import ExternalLink from '../../../../assets/svg/Feather/ExternalLink';
import ChevronDown from '../../../../assets/svg/Feather/ChevronDown';
import HuntUnitsIcon from '../../../../assets/svg/LayerIcons/HuntUnitsIcon';
import ghAnalytics from '../../../../lib/analytics';
import { getConfig } from '../../../../lib/config';

function formatSpecies(species) {
  if (!species) return null;
  return species.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function extractUnitCards(feature) {
  const attrs = feature.attributes;
  const baseUrl = getConfig('REACT_APP_GOHUNT_CMS_URL') || 'https://www.gohunt.com';

  if (!attrs.up_data) return [];

  try {
    const units = JSON.parse(attrs.up_data);
    if (!Array.isArray(units)) return [];
    return units
      .filter(u => u.url)
      .map(u => {
        const url = u.url.startsWith('http') ? u.url : `${baseUrl}${u.url}`;
        const title = u.unit_name ? String(u.unit_name) : null;
        const species = formatSpecies(u.species);
        return { title, species, url };
      });
  } catch (_) {
    return [];
  }
}

function HuntUnitCard({ card, stateName }) {
  const handleViewProfile = () => {
    if (!card.url) return;
    ghAnalytics.logAboutLocationHuntUnitClick(card.title, card.species, stateName);
    const tab = window.open(card.url, '_blank');
    if (tab) tab.focus();
  };

  return (
    <div className={styles.huntUnitCard}>
      <div className={styles.huntUnitCardTitle}>{card.title}</div>
      {card.species && (
        <div className={styles.huntUnitCardDetail}>{card.species}</div>
      )}
      <button
        className={styles.viewProfileButton}
        onClick={handleViewProfile}
        disabled={!card.url}
      >
        <ExternalLink width={14} height={14} />
        View unit profile
      </button>
    </div>
  );
}

HuntUnitCard.propTypes = {
  card: PropTypes.object,
  stateName: PropTypes.string,
};

function HuntUnitsSection({ isLoading, huntUnitsAtLocation }) {
  const [isExpanded, setIsExpanded] = useState(true);

  const allCards = [];
  const seen = new Set();
  let stateName = null;

  if (!isLoading) {
    for (const { layer, metadata } of huntUnitsAtLocation) {
      if (!stateName) stateName = layer.state;
      for (const feature of metadata.features) {
        const cards = extractUnitCards(feature);
        for (const card of cards) {
          const dedupeKey = card.url || `${card.title}|${card.species}`;
          if (!seen.has(dedupeKey)) {
            seen.add(dedupeKey);
            allCards.push(card);
          }
        }
      }
    }
  }

  let body = null;
  if (isLoading) {
    body = (
      <div className={styles.huntUnitsBody}>
        <div className={styles.loadingCard} style={{ padding: '0' }}>
          <div className={styles.loadingSpinner} />
          <span>Loading hunt units</span>
        </div>
      </div>
    );
  } else if (allCards.length > 0) {
    body = (
      <div className={styles.huntUnitsBody}>
        {allCards.map((card, i) => (
          <HuntUnitCard
            key={`hunt-card-${i}-${card.url || `${card.title}-${card.species}`}`}
            card={card}
            stateName={stateName}
          />
        ))}
      </div>
    );
  } else {
    body = (
      <div className={styles.huntUnitsBody}>
        <div className={styles.emptyState}>We currently do not offer hunt unit profiles for this state.</div>
      </div>
    );
  }

  return (
    <div className={styles.huntUnitsOuter}>
      <div className={styles.huntUnitsHeader} onClick={() => setIsExpanded(v => !v)}>
        <HuntUnitsIcon />
        <span className={styles.huntUnitsTitle}>Hunt Units</span>
        <span className={`${styles.huntUnitsChevron} ${isExpanded ? styles.huntUnitsChevronOpen : ''}`}>
          <ChevronDown width={18} color='#B5B5B8' />
        </span>
      </div>
      {isExpanded && body}
    </div>
  );
}

HuntUnitsSection.propTypes = {
  isLoading: PropTypes.bool,
  huntUnitsAtLocation: PropTypes.array,
};

export default HuntUnitsSection;
